import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseFectch';

export interface Project {
  _id: string;
  name: string;
  description?: string;
  companyId: string;
  manager?: string;
  status: 'planning' | 'active' | 'on_hold' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high';
  startDate?: string;
  endDate?: string;
  budget?: number;
  tags?: string[];
  clientId?: string;
  clientName?: string;
  isBillable?: boolean;
  billableRate?: number;
  currency?: string;
  agencyId?: string;
  createdAt: string;
  updatedAt: string;
  managerDetails?: {
    name: string;
    email: string;
    role: string;
  };
}

export interface Sprint {
  _id: string;
  name: string;
  description?: string;
  projectId: string;
  goal?: string;
  status: 'planned' | 'active' | 'completed' | 'cancelled';
  startDate: string;
  endDate: string;
  capacity?: number;
  createdAt: string;
  updatedAt: string;
  projectDetails?: {
    name: string;
  };
}

export interface Task {
  _id: string;
  title: string;
  description?: string;
  projectId: string;
  sprintId?: string;
  assignee?: string;
  reporter: string;
  status: 'backlog' | 'todo' | 'in_progress' | 'in_review' | 'done' | 'blocked' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  dueDate?: string;
  estimatedHours?: number;
  actualHours?: number;
  tags?: string[];
  dependsOn?: string[];
  isBillable?: boolean;
  billableRate?: number;
  clientId?: string;
  projectPhase?: string;
  createdAt: string;
  updatedAt: string;
  projectDetails?: {
    name: string;
  };
  sprintDetails?: {
    name: string;
  };
  assigneeDetails?: {
    name: string;
    email: string;
    role: string;
  };
  reporterDetails?: {
    name: string;
    email: string;
    role: string;
  };
}

export interface ProjectStats {
  totalProjects: number;
  activeProjects: number;
  completedProjects: number;
  onHoldProjects: number;
  totalBudget: number;
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  totalSprints: number;
  activeSprints: number;
}

export const projectApi = createApi({
  reducerPath: 'projectApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Projects', 'Sprints', 'Tasks', 'ProjectStats'],
  endpoints: (builder) => ({
    getProjects: builder.query<{ success: boolean; data: Project[] }, { status?: string; manager?: string; search?: string } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.status) params.append('status', args.status);
        if (args?.manager) params.append('manager', args.manager);
        if (args?.search) params.append('search', args.search);
        const qs = params.toString();
        return {
          url: `/projects${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Projects'],
    }),

    getProjectById: builder.query<{ success: boolean; data: Project }, string>({
      query: (id) => ({
        url: `/projects/${id}`,
        method: 'GET',
      }),
      providesTags: ['Projects'],
    }),

    createProject: builder.mutation<{ success: boolean; data: Project }, any>({
      query: (body) => ({
        url: '/projects',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Projects', 'ProjectStats'],
    }),

    updateProject: builder.mutation<{ success: boolean; data: Project }, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/projects/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Projects', 'ProjectStats'],
    }),

    deleteProject: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Projects', 'ProjectStats', 'Sprints', 'Tasks'],
    }),

    getSprints: builder.query<{ success: boolean; data: Sprint[] }, { projectId?: string; status?: string; search?: string } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.projectId) params.append('projectId', args.projectId);
        if (args?.status) params.append('status', args.status);
        if (args?.search) params.append('search', args.search);
        const qs = params.toString();
        return {
          url: `/sprints${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Sprints'],
    }),

    getSprintById: builder.query<{ success: boolean; data: Sprint }, string>({
      query: (id) => ({
        url: `/sprints/${id}`,
        method: 'GET',
      }),
      providesTags: ['Sprints'],
    }),

    createSprint: builder.mutation<{ success: boolean; data: Sprint }, any>({
      query: (body) => ({
        url: '/sprints',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Sprints', 'ProjectStats'],
    }),

    updateSprint: builder.mutation<{ success: boolean; data: Sprint }, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/sprints/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Sprints', 'ProjectStats'],
    }),

    deleteSprint: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/sprints/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Sprints', 'ProjectStats', 'Tasks'],
    }),

    getTasks: builder.query<{ success: boolean; data: Task[] }, { projectId?: string; sprintId?: string; assignee?: string; status?: string; priority?: string; search?: string; employeeId?: string } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.projectId) params.append('projectId', args.projectId);
        if (args?.sprintId) params.append('sprintId', args.sprintId);
        if (args?.assignee) params.append('assignee', args.assignee);
        if (args?.status) params.append('status', args.status);
        if (args?.priority) params.append('priority', args.priority);
        if (args?.search) params.append('search', args.search);
        if (args?.employeeId) params.append('employeeId', args.employeeId);
        const qs = params.toString();
        return {
          url: `/tasks${qs ? `?${qs}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Tasks'],
    }),

    getTaskById: builder.query<{ success: boolean; data: Task }, string>({
      query: (id) => ({
        url: `/tasks/${id}`,
        method: 'GET',
      }),
      providesTags: ['Tasks'],
    }),

    createTask: builder.mutation<{ success: boolean; data: Task }, any>({
      query: (body) => ({
        url: '/tasks',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Tasks', 'ProjectStats'],
    }),

    updateTask: builder.mutation<{ success: boolean; data: Task }, { id: string; body: any }>({
      query: ({ id, body }) => ({
        url: `/tasks/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Tasks', 'ProjectStats'],
    }),

    deleteTask: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/tasks/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Tasks', 'ProjectStats'],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetProjectByIdQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useGetSprintsQuery,
  useGetSprintByIdQuery,
  useCreateSprintMutation,
  useUpdateSprintMutation,
  useDeleteSprintMutation,
  useGetTasksQuery,
  useGetTaskByIdQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
} = projectApi;

export default projectApi;
